import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { Modal, Form, Select, DatePicker, Badge, Input, message, Popconfirm, TimePicker as AntTimePicker, Tag, Switch } from "antd";
import { Button, Card, Col, Row } from "react-bootstrap";
import dayjs from "dayjs";
import 'dayjs/locale/tr';
import 'dayjs/locale/en';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { useUsers } from "../../../hooks/useUsers";
import Table from "../../../components/shared/dataTable";
import { ArrowLeftOutlined, ArrowRightOutlined, EditOutlined, DeleteOutlined, CameraOutlined, FilePdfOutlined } from "@ant-design/icons";
import { User } from "../../../types/user.types";
import shiftService from "../../../services/shift/shift.service";
import { ShiftInfo, ShiftAssignmentCreateDTO, UserWithShifts, UserShift, ShiftCreateDTO } from "../../../types/shift.types";
import './styles/style.css';
import Swal from "sweetalert2";
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import FooterSection from "../../shared/components/footerSection";
import i18n from "../../../i18n/config";
import trTR from "antd/lib/calendar/locale/tr_TR";
import enUS from "antd/lib/calendar/locale/en_US";
import datePickerTr from 'antd/lib/date-picker/locale/tr_TR';
import datePickerEn from 'antd/lib/date-picker/locale/en_US';
import { useEmployee } from "../../../hooks/useEmployee";

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

dayjs.locale('tr');

// Vardiya renk kodları
const shiftColors: Record<string, string> = {
  default: "#2196F3", // Varsayılan mavi
};

// Günlük atama tipi
interface DailyAssignment extends UserShift {
  userId: number;
  userName: string;
}

const ShiftPage: React.FC = () => {
  const { t } = useTranslation();
  const { users, loading: usersLoading } = useUsers();
  const { departments, shops, fetchDepartmentsAndShops } = useEmployee();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [currentWeek, setCurrentWeek] = useState<dayjs.Dayjs[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDay, setSelectedDay] = useState<dayjs.Dayjs | null>(null);
  const [form] = Form.useForm();
  const [dailyAssignments, setDailyAssignments] = useState<DailyAssignment[]>([]);
  
  // Haftalık takvim referansı
  const calendarRef = useRef<HTMLDivElement>(null);
  
  // Vardiya tipleri ve atamaları için state'ler
  const [shifts, setShifts] = useState<ShiftInfo[]>([]);
  const [weeklyAssignments, setWeeklyAssignments] = useState<UserWithShifts[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Vardiya oluşturma modalı için state'ler
  const [isShiftTypeModalVisible, setIsShiftTypeModalVisible] = useState(false);
  const [shiftTypeForm] = Form.useForm();
  const [editingShift, setEditingShift] = useState<ShiftInfo | null>(null);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [isAllDepartments, setIsAllDepartments] = useState(false);
  const [isAllBranches, setIsAllBranches] = useState(false);
  
  // Toplu vardiya atama modalı için state'ler
  const [isBulkAssignModalVisible, setIsBulkAssignModalVisible] = useState(false);
  const [bulkAssignForm] = Form.useForm();
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);


  useEffect(() => {
    fetchDepartmentsAndShops();
  }, [fetchDepartmentsAndShops]);

  // Filtre işlevleri
  const handleDepartmentFilter = (value: number[]) => {
    setSelectedDepartments(value);
  };

  const handleBranchFilter = (value: number[]) => {
    setSelectedBranches(value);
  };

  const clearFilters = () => {
    setSelectedDepartments([]);
    setSelectedBranches([]);
  };

  // Kullanıcıların filtre ile eşleşip eşleşmediğini kontrol et
  const isUserMatchesFilter = (user: any, userDepartments?: any[], userBranches?: any[]) => {
    const departments = userDepartments || user?.department || [];
    const branches = userBranches || user?.branch || [];
    
    const userDepartmentIds = departments?.map((d: any) => d.id);
    const userBranchIds = branches?.map((b: any) => b.id);

    const departmentMatch = selectedDepartments?.length === 0 || 
      selectedDepartments?.some(id => userDepartmentIds.includes(id));
    
    const branchMatch = selectedBranches?.length === 0 || 
      selectedBranches?.some(id => userBranchIds.includes(id));

    return departmentMatch && branchMatch;
  };
  
  const filteredUsers = users?.filter(user => 
    isUserMatchesFilter(user, user.department, user.branch)
  ) || [];

  // Filtrelenmiş haftalık atamalar
  const filteredWeeklyAssignments = weeklyAssignments.filter(assignment => {
    // User bilgisini bul
    const user = users?.find(u => u.id === assignment.userId);
    if (!user) return false;
    return isUserMatchesFilter(user, user.department, user.branch);
  });

  useEffect(() => {
    dayjs.locale(i18n.language); // örn. 'tr' veya 'en'
  }, [i18n.language]);

  // 2) AntD ConfigProvider için genel locale
  const antdLocale = useMemo(() => {
    return i18n.language === 'tr' ? trTR : enUS;
  }, [i18n.language]);

  // 3) Sadece DatePicker'ın iç locale'i
  const pickerLocale = useMemo(() => {
    return i18n.language === 'tr' ? datePickerTr : datePickerEn;
  }, [i18n.language]);


  // Haftalık tarihleri hesapla
  useEffect(() => {
    const startOfWeek = selectedDate.startOf('week');
    const weekDays = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));
    setCurrentWeek(weekDays);
  }, [selectedDate]);

  // Vardiyaları ve haftalık atamaları getir
  const fetchShifts = useCallback(async () => {
    try {
      setIsLoading(true);
      const shiftsData = await shiftService.getCompanyShifts();
      setShifts(shiftsData);
      
      // Vardiya renklerini ayarla
      shiftsData.forEach((shift, index) => {
        //console.log(shift);
        
        const hue = (index * 137) % 360; // Altın oran ile çeşitli renkler
        shiftColors[shift.id.toString()] = `hsl(${hue}, 70%, 50%)`;
      });
      
    } catch (err: any) {
      handleApiError(err,t);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchWeeklyAssignments = useCallback(async () => {
    if (currentWeek?.length === 0) return;
    try {
      setIsLoading(true);
      const startDate = currentWeek[0].format('YYYY-MM-DD');
      const endDate = currentWeek[6].format('YYYY-MM-DD');
      
      const assignmentsData = await shiftService.getShiftAssignments(startDate, endDate);
      //console.log(assignmentsData);
      
      setWeeklyAssignments(assignmentsData);
      return assignmentsData;
    } catch (err: any) {
      handleApiError(err,t);
    } finally {
      setIsLoading(false);
    }
  }, [currentWeek,t]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  useEffect(() => {
    if (currentWeek?.length > 0) {
      fetchWeeklyAssignments();
    }
  }, [currentWeek, fetchWeeklyAssignments]);

  const getFilteredDayAssignments = (day: dayjs.Dayjs): DailyAssignment[] => {
    const dayString = day.format('YYYY-MM-DD');
    const assignments: DailyAssignment[] = [];
    const currentDate = dayjs(dayString);

    filteredWeeklyAssignments.forEach(user => {
      user.shifts.forEach(shift => {
        const shiftStartDate = dayjs(shift.startDate);
        const shiftEndDate = dayjs(shift.endDate);

        // Vardiya bilgisini bul
        const shiftInfo = shifts.find(s => s.id === shift.shiftId);
        if(!shiftInfo) return;

        const validFrom = shiftInfo.validFrom ? dayjs(shiftInfo.validFrom) : null;
        const validTo = shiftInfo.validTo ? dayjs(shiftInfo.validTo) : null;

        if(validFrom && validTo){
          if(!(currentDate.isAfter(validFrom) || currentDate.isSame(validFrom, 'day')) || 
          !(currentDate.isBefore(validTo) || currentDate.isSame(validTo, 'day'))){  
            return;
          }
        }

        // Eğer vardiya arşivlenmişse ve güncel tarih arşivlenme tarihinden sonraysa, gösterme
        if (shiftInfo?.status === 'archived') {
          const updateDate = dayjs(shiftInfo.updatedAt);
          if (currentDate.isAfter(updateDate)) {
            return;
          }
        }

        // Tarih aralığı kontrolü
        if (currentDate.isSame(shiftStartDate, 'day') || 
            (currentDate.isAfter(shiftStartDate) && currentDate.isBefore(shiftEndDate)) || 
            currentDate.isSame(shiftEndDate, 'day')) {
          assignments.push({
            ...shift,
            userId: user.userId,
            userName: user.userName
          });
        }
      });
    });

    return assignments;
  };

  const getDayAssignments = (day: dayjs.Dayjs): DailyAssignment[] => {
    return getFilteredDayAssignments(day);
  };
  // Gün seçildiğinde modal'ı aç
  const handleDayClick = (day: dayjs.Dayjs) => {
    setSelectedDay(day);
    setIsModalVisible(true);
    
    // Seçilen güne ait vardiyaları getir
    const assignmentsForDay = getDayAssignments(day);
    setDailyAssignments(assignmentsForDay);
  };

  // Önceki/sonraki hafta navigasyonu
  const goToPreviousWeek = () => {
    setSelectedDate(selectedDate.subtract(1, 'week'));
  };

  const goToNextWeek = () => {
    setSelectedDate(selectedDate.add(1, 'week'));
  };

  // Modal kapatma
  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  // Vardiya atama
  const handleAssignShift = async () => {
    try {
      const values = await form.validateFields();
      //console.log(values);
      
      // Seçilen gün için vardiya ataması
      const selectedDate = dayjs(values.date).format('YYYY-MM-DD');
      const userIds = values.employeeId;
      const shiftId = values.shiftId;
      const today = dayjs().startOf('day');
    
// Seçilen tarihin başlangıcını al (saat, dakika, saniye sıfır)
      const selectedDay = dayjs(selectedDate).startOf('day');
          
      // Seçilen tarih bugünden önceyse (bugün hariç)
      if(selectedDay.isBefore(today)){
          message.error(t("shifts.error.pastDate"));
          return;
      }
      const usersWithExistingAssignments: number[] = [];
    
      userIds.forEach((userId: number) => {
        const existingAssigmentOnDay = weeklyAssignments.find(user => 
          user.userId === userId &&
          user.shifts.some(shift => 
            dayjs(shift.startDate).format('YYYY-MM-DD') === selectedDate
          )
        );
        
        if (existingAssigmentOnDay) {
          usersWithExistingAssignments.push(userId);
        }
      });
  
      // Eğer bazı kullanıcıların zaten ataması varsa uyarı göster
      if (usersWithExistingAssignments.length > 0) {
        const usersWithAssignments = usersWithExistingAssignments.map(userId => {
          const user = filteredUsers.find(u => u.id === userId);
          return `${user?.firstName} ${user?.lastName}`;
        }).join(', ');
  
        const result = await Swal.fire({
          icon: 'warning',
          title: t("shifts.warning.existingAssignment"),
          text: `${usersWithAssignments} ${t("shifts.warning.existingAssignmentDescription")}`,
          confirmButtonText: t("shifts.warning.confirmButtonText"),
          cancelButtonText: t("shifts.warning.cancelButtonText"),
          showCancelButton: true,
          showConfirmButton: true,
        });
  
        if (!result.isConfirmed) {
          return;
        }
      }
  
      setIsLoading(true);
      
      // Başarılı ve başarısız atamalar için sayaçlar
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
  
      // Her kullanıcı için ayrı ayrı vardiya ataması yap
      for (const userId of userIds) {
        try {
          const assignmentData: ShiftAssignmentCreateDTO = {
            userId: userId,
            shiftId: shiftId,
            startDate: selectedDate,
            endDate: selectedDate,
            startTime: values.startTime,
            endTime: values.endTime
          };
  
          await shiftService.assignShift(assignmentData);
          successCount++;
        } catch (error: any) {
          errorCount++;
          const user = filteredUsers.find(u => u.id === userId);
          const userName = `${user?.firstName} ${user?.lastName}`;
          errors.push(`${userName}: ${error.message || 'Bilinmeyen hata'}`);
        }
      }
  
      // Sonuç mesajlarını göster
      if (successCount > 0) {
        message.success(`${successCount} kişiye vardiya başarıyla atandı`);
      }
      
      if (errorCount > 0) {
        message.error(`${errorCount} kişiye vardiya atanamadı`);
        // Detaylı hata mesajlarını konsola yazdır
        console.error('Vardiya atama hataları:', errors);
      }
      
      // Atamaları yenile
      if (currentWeek?.length > 0) {
        await fetchWeeklyAssignments();
      }
      
      // Modal içindeki tabloyu da güncellemek için
      if (selectedDay) {
        const assignmentsForDay = getDayAssignments(selectedDay);
        setDailyAssignments(assignmentsForDay);
      }
      
      setIsModalVisible(false);
      form.resetFields();
    } catch (err: any) {
      handleApiError(err, t);
    } finally {
      setIsLoading(false);
    }
  };
  

  // Toplu vardiya atama fonksiyonu
  const handleBulkAssignShift = async () => {
    try {
      const values = await bulkAssignForm.validateFields();
      
      if (!dateRange || dateRange.length !== 2) {
        message.error("Lütfen tarih aralığı seçiniz");
        return;
      }

      if (selectedDays.length === 0) {
        message.error("Lütfen en az bir gün seçiniz");
        return;
      }
      
      const startDate = dateRange[0];
      const endDate = dateRange[1];
      const userIds = values.employeeIds;
      const shiftId = values.shiftId;

      // Onay dialogu göster
      const result = await Swal.fire({
        icon: 'question',
        title: "Toplu Atama Onayı",
        html: `
          <div style="text-align: left;">
            <p><strong>Çalışan Sayısı:</strong> ${userIds.length}</p>
            <p><strong>Tarih Aralığı:</strong> ${startDate.format('DD.MM.YYYY')} - ${endDate.format('DD.MM.YYYY')}</p>
            <p><strong>Seçilen Günler:</strong> ${selectedDays.map(day => {
              const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
              return dayNames[parseInt(day)];
            }).join(', ')}</p>
            <p><strong>Toplam Atama:</strong> ${userIds.length} çalışan</p>
          </div>
        `,
        confirmButtonText: "Evet, Atama Yap",
        cancelButtonText: "İptal",
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      });

      if (!result.isConfirmed) {
        return;
      }

      setIsLoading(true);
      
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Her kullanıcı için toplu atama yap
      for (const userId of userIds) {
        try {
          // Seçilen günleri İngilizce gün isimlerine çevir
          const dayMapping: { [key: string]: string } = {
            '0': 'Sunday',
            '1': 'Monday', 
            '2': 'Tuesday',
            '3': 'Wednesday',
            '4': 'Thursday',
            '5': 'Friday',
            '6': 'Saturday'
          };
          
          const englishDays = selectedDays.map(day => dayMapping[day]);

          const assignmentData: ShiftAssignmentCreateDTO = {
            userId: userId,
            shiftId: shiftId,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            selectedDays: englishDays // Backend'e İngilizce gün isimleri
          };

          await shiftService.assignShift(assignmentData);
          successCount++;
        } catch (error: any) {
          errorCount++;
          const user = filteredUsers.find(u => u.id === userId);
          const userName = `${user?.firstName} ${user?.lastName}`;
          errors.push(`${userName}: ${error.message || 'Bilinmeyen hata'}`);
        }
      }

      // Sonuç mesajlarını göster
      if (successCount > 0) {
        message.success(`${successCount} atama başarıyla yapıldı`);
      }
      
      if (errorCount > 0) {
        message.error(`${errorCount} atama başarısız oldu`);
        console.error('Toplu vardiya atama hataları:', errors);
      }
      
      // Atamaları yenile
      if (currentWeek?.length > 0) {
        await fetchWeeklyAssignments();
      }
      
      setIsBulkAssignModalVisible(false);
      bulkAssignForm.resetFields();
      setSelectedDays([]);
      setDateRange(null);
    } catch (err: any) {
      handleApiError(err, t);
    } finally {
      setIsLoading(false);
    }
  };

  // Vardiya tipi oluştur/düzenle modalını aç
  const openShiftTypeModal = (shift: ShiftInfo | null = null) => {
    setEditingShift(shift);
    if (shift) {
      const allDepartments = !shift.departments || shift.departments.length === 0 || 
        shift.departments.length === departments?.length;
      const allBranches = !shift.branches || shift.branches.length === 0 || 
        shift.branches.length === shops?.length;

      setIsAllDepartments(allDepartments);
      setIsAllBranches(allBranches);
      shiftTypeForm.setFieldsValue({
        name: shift.name,
        startTime: dayjs(shift.startTime, 'HH:mm'),
        endTime: dayjs(shift.endTime, 'HH:mm'),
        status: shift.status,
        validFrom: shift.validFrom ? dayjs(shift.validFrom) : null,
        validTo: shift.validTo ? dayjs(shift.validTo) : null,
        departments: allDepartments ? [] : shift.departments?.map((d: any) => d.id),
        branches: allBranches ? [] : shift.branches?.map((b: any) => b.id)
      });
    } else {
      setIsAllDepartments(true);
      setIsAllBranches(true);
      shiftTypeForm.resetFields();
      shiftTypeForm.setFieldsValue({
        status: 'active',
        departments: [],
        branches: []
      });  
    }
    setIsShiftTypeModalVisible(true);
  };

  // Toplu vardiya atama modalını aç
  const openBulkAssignModal = () => {
    setIsBulkAssignModalVisible(true);
    setSelectedDays([]);
    setDateRange(null);
    bulkAssignForm.resetFields();
  };

  // Vardiya tipi oluştur/düzenle
  const handleShiftTypeSubmit = async () => {
    try {
      const values = await shiftTypeForm.validateFields();
      
      // Switch durumuna göre departman ve şube ID'lerini belirle
      let selectedDepartmentIds: number[] = [];
      let selectedBranchIds: number[] = [];
  
      if (isAllDepartments) {
        // Tüm departmanlar seçiliyse, tüm departman ID'lerini ekle
        selectedDepartmentIds = departments?.map(dept => dept.id) || [];
      } else if (values.departments) {
        selectedDepartmentIds = values.departments;
      }
      
      if (isAllBranches) {
        // Tüm şubeler seçiliyse, tüm şube ID'lerini ekle
        selectedBranchIds = shops?.map(shop => shop.id) || [];
      } else if (values.branches) {
        selectedBranchIds = values.branches;
      }
  
      const shiftData: ShiftCreateDTO = {
        name: values.name,
        startTime: values.startTime?.format ? values.startTime.format('HH:mm') : values.startTime,
        endTime: values.endTime?.format ? values.endTime.format('HH:mm') : values.endTime,
        status: values.status,
        validFrom: values.validFrom?.format ? values.validFrom.format('YYYY-MM-DD') : values.validFrom,
        validTo: values.validTo?.format ? values.validTo.format('YYYY-MM-DD') : values.validTo,
        departmentIds: selectedDepartmentIds,
        branchIds: selectedBranchIds
      };
      
      setIsLoading(true);
      
      if (editingShift) {
        await shiftService.updateShift(editingShift.id, shiftData);
        message.success(t("shifts.success.shiftTypeUpdated"));
      } else {
        await shiftService.createShift(shiftData);
        message.success(t("shifts.success.shiftTypeCreated"));
      }
      
      fetchShifts();
      setIsShiftTypeModalVisible(false);
      shiftTypeForm.resetFields();
      setIsAllDepartments(false);
      setIsAllBranches(false);
    } catch (errorInfo: any) {
      if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
        // Validasyon hatası
      } else {
        handleApiError(errorInfo, t);
      }    
    } finally {
      setIsLoading(false);
    }
  };

  // Vardiya tipini sil
  const handleDeleteShift = async (id: number) => {
    try {
      setIsLoading(true);
      await shiftService.deleteShift(id);
      message.success(t("shifts.success.shiftTypeDeleted"));
      fetchShifts();
    } catch (err: any) {
      handleApiError(err,t);
    } finally {
      setIsLoading(false);
    }
  };

  // Ekran görüntüsü alma fonksiyonu
  const captureScreenshot = async () => {
    if (!calendarRef.current) return;
    
    try {
      message.loading({
        content: t("shifts.loading.screenshot"),
        key: "screenshot",
      });
      
      const canvas = await html2canvas(calendarRef.current, {
        scale: 2, // Daha yüksek kalite için
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff"
      });
      
      // Canvas'ı resme dönüştür
      const imageUrl = canvas.toDataURL("image/png");
      
      // Resmi indir
      const link = document.createElement("a");
      link.download = `${t("shifts.pdf.title")}-${currentWeek[0].format('DD-MM-YYYY')}-${currentWeek[6].format('DD-MM-YYYY')}.png`;
      link.href = imageUrl;
      link.click();
      
      message.success({
        content: t("shifts.success.screenshotSaved"),
        key: "screenshot",
      });
    } catch (error) {
      console.error("Ekran görüntüsü alınırken hata oluştu:", error);
      message.error({
        content: t("shifts.error.screenshotError"),
        key: "screenshot",
      });
    }
  };
  
  // PDF oluşturma fonksiyonu
  const exportToPdf = async () => {
    if (!calendarRef.current) return;
    
    try {
      message.loading({
        content: t("shifts.loading.pdf"),
        key: "pdf",
      });
      
      const canvas = await html2canvas(calendarRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      
      // Oluşturulan resmin boyutlarını al
      const imgWidth = 210; // A4 genişliği (mm)
      const pageHeight = 297; // A4 yüksekliği (mm)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      // PDF oluştur
      const pdf = new jsPDF("p", "mm", "a4");
      let position = 20;
      
      // İlk sayfayı ekle
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Gerekirse birden fazla sayfa ekle
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Başlık ekle
      pdf.setFontSize(16);
      pdf.text(`${t("shifts.pdf.title")} (${currentWeek[0].format('DD.MM.YYYY')} - ${currentWeek[6].format('DD.MM.YYYY')})`, 105, 15, { align: 'center' });
      
      // PDF'i indir
      pdf.save(`${t("shifts.pdf.title")}-${currentWeek[0].format('DD-MM-YYYY')}-${currentWeek[6].format('DD-MM-YYYY')}.pdf`);
      
      message.success({
        content: t("shifts.success.pdfSaved"),
        key: "pdf",
      });
    } catch (error) {
      console.error("PDF oluşturulurken hata oluştu:", error);
      message.error({
        content: t("shifts.error.pdfError"),
        key: "pdf",
      });
    }
  };

  // Vardiya dağılımı için grafik verileri
  const filteredPieChartData = {
    labels: shifts.filter(shift => shift.status==='active').map(shift => `${shift.name}`),
    datasets: [
      {
        data: shifts.filter(shift => shift.status==='active').map(shift => {
          return filteredWeeklyAssignments.reduce((count, user) => 
            count + user.shifts.filter((s: any) => s.shiftId === shift.id).length, 0
          );
        }),
        backgroundColor: shifts.filter(shift => shift.status==='active').map(shift => {
          const color = shiftColors[shift?.id?.toString()] || shiftColors.default;
          return color;
        }),
        borderWidth: 1,
      },
    ],
  };

  // Günlük vardiya dağılımı için grafik verileri
  const filteredBarChartData = {
    labels: currentWeek.map(day => day.format('dddd')),
    datasets: [
      {
        label: t("shifts.barChart.label"),
        data: currentWeek.map((day: any) => getFilteredDayAssignments(day).length),
        backgroundColor: '#4CAF50',
      },
    ],
  };
  const handleDeleteShiftAssignment = async (assignmentId: number) => {
    try {
      setIsLoading(true);
      
      await shiftService.deleteShiftAssignment(assignmentId); 
      message.success(t("shifts.success.shiftAssignmentDeleted"));
      
      // Atamaları yenile
      if (currentWeek?.length > 0) {
        await fetchWeeklyAssignments(); // await ekledim
      }
      // Modal içindeki tabloyu da güncellemek için dailyAssignments'ı yeniden filtrele
      if (selectedDay) {
        const assignmentsForDay = getDayAssignments(selectedDay);
        setDailyAssignments(assignmentsForDay);
      }

      setDailyAssignments(prevDailyAssignments => 
        prevDailyAssignments.filter(assignment => assignment.id !== assignmentId)
      );


    } catch (err: any) {
      handleApiError(err, t);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Vardiya tablosu için sütunlar
  const shiftColumns = [
    {
      title: t("shifts.table.employee"),
      dataIndex: "userName",
      sorter: (a: DailyAssignment, b: DailyAssignment) => a.userName.localeCompare(b.userName)
    },
    {
      title: t("shifts.table.shift"),
      render: (record: DailyAssignment) => {
        const shiftInfo = shifts.find(s => s.id === record.shiftId);
        return (
          <div>
            {shiftInfo?.name}
            {shiftInfo?.status === 'archived' && (
              <Badge 
                style={{ marginLeft: 8 }}
                count={t("shifts.table.archived")} 
                color="default"
              />
            )}
          </div>
        );
      }
    },
    {
      title: t("shifts.table.time"),
      render: (record: DailyAssignment) => `${record.startTime} - ${record.endTime}`
    },
    {
      title: t("shifts.table.actions"),
      key: 'actions',
      render: (_: any, record: DailyAssignment) => (
        <Popconfirm
          title={t("shifts.table.deleteAssignmentConfirmation")}
          onConfirm={() => handleDeleteShiftAssignment(record.id)} // record.id'nin atama ID'si olduğunu varsayıyoruz
          okText={t("shifts.table.confirmButtonText")}
          cancelText={t("shifts.table.cancelButtonText")}
          disabled={isLoading} // Yükleme sırasında butonu devre dışı bırak
        >
          <Button variant="danger" size="sm">
            <DeleteOutlined />
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // Vardiya tipleri modalı için sütunlar
  const shiftTypeColumns = [
    {
      title: t("shifts.table.name"),
      dataIndex: "name",
      sorter: (a: ShiftInfo, b: ShiftInfo) => a.name.localeCompare(b.name),
    },
    {
      title: t("shifts.table.startTime"),
      dataIndex: "startTime",
      sorter: (a: ShiftInfo, b: ShiftInfo) => a.startTime.localeCompare(b.startTime),
    },
    {
      title: t("shifts.table.endTime"),
      dataIndex: "endTime",
      sorter: (a: ShiftInfo, b: ShiftInfo) => a.endTime.localeCompare(b.endTime),
    },
    {
      title: "Departmanlar",
      render: (record: ShiftInfo) => (
        <div>
          {record.departments && record.departments.length > 0 
            ? record.departments.map((d: any) => d.name).join(', ')
            : 'Tüm Departmanlar'
          }
        </div>
      ),
    },
    {
      title: "Şubeler", 
      render: (record: ShiftInfo) => (
        <div>
          {record.branches && record.branches.length > 0
            ? record.branches.map((b: any) => b.name).join(', ')
            : 'Tüm Şubeler'
          }
        </div>
      ),
    },
    {
      title: t("shifts.table.status"),
      dataIndex: "status",
      render: (status: string) => (
        <Badge 
          status={status === 'active' ? 'success' : 'default'} 
          text={status === 'active' ? t("shifts.table.active") : t("shifts.table.archived")} 
        />
      ),
    },
    {
      title: t("shifts.table.lastUpdate"),
      dataIndex: "updated",
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
      sorter: (a: ShiftInfo, b: ShiftInfo) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
    },
    {
      title: t("shifts.table.actions"),
      render: (_: any, record: ShiftInfo) => (
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => openShiftTypeModal(record)}
          >
            <EditOutlined />
          </Button>
          <Popconfirm
            title={t("shifts.table.deleteConfirmation")}
            onConfirm={() => handleDeleteShift(record.id)}
            okText={t("shifts.table.confirmButtonText")}
            cancelText={t("shifts.table.cancelButtonText")}
          >
            <Button variant="danger" size="sm">
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];
  // İstatistik kartlarının tasarımını iyileştir
  const StatisticCard = ({ 
    title, 
    value, 
    subtitle = "", 
    icon, 
    iconBg, 
    trend = null 
  }: { 
    title: string, 
    value: string | number, 
    subtitle?: string, 
    icon: React.ReactNode, 
    iconBg: string,
    trend?: { value: number, isPositive: boolean } | null 
  }) => (
    <Card className="h-100 border-0 shadow-sm">
      <Card.Body className="d-flex align-items-center p-4">
        <div 
          className="me-3 rounded-circle d-flex align-items-center justify-content-center"
          style={{ 
            width: "60px", 
            height: "60px", 
            backgroundColor: iconBg,
            color: "white"
          }}
        >
          {icon}
        </div>
        <div>
          <h6 className="text-muted mb-1 fs-sm">{title}</h6>
          <h3 className="mb-0 fw-bold">{value}</h3>
          {subtitle && <small className="text-muted">{subtitle}</small>}
          {trend && (
            <div className={`mt-2 text-${trend.isPositive ? 'success' : 'danger'} d-flex align-items-center`}>
              <i className={`ti ti-trend-${trend.isPositive ? 'up' : 'down'} me-1`}></i>
              <span>{trend.value}% {t("shifts.barChart.trend")}</span>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  // Günlük vardiyaları sınırla ve göster
  const renderDayAssignments = (day: dayjs.Dayjs) => {
    const assignments = getDayAssignments(day);
    
    if (assignments?.length === 0) {
      return <div className="text-muted text-center mt-2">{t("shifts.table.noShifts")}</div>;
    }
    
    // Maksimum 3 kişi göster
    const visibleAssignments = assignments.slice(0, 3);
    const remainingCount = assignments?.length - 3;
    
    return (
      <>
        {visibleAssignments.map((assignment, assignmentIndex) => {
          const shift = shifts.find(s => s.id === assignment.shiftId);
          return shift && assignment.userName ? (
            <div 
              key={assignmentIndex} 
              className="mb-1 p-1 rounded"
              style={{ backgroundColor: `${shiftColors[shift.id.toString()] || shiftColors.default}20` }}
            >
              <Badge 
                color={shiftColors[shift.id.toString()] || shiftColors.default} 
                text={`${assignment.userName} (${shift.name})`} 
              />
            </div>
          ) : null;
        })}
        
        {remainingCount > 0 && (
          <div className="text-center mt-2">
            <Badge color="blue" text={`+ ${remainingCount} ${t("shifts.table.more")}`} />
          </div>
        )}
      </>
    );
  };

  // Haftalık görünüm için vardiyaları filtrele
  const getFilteredShifts = useCallback((): ShiftInfo[] => {
    if (!currentWeek?.length) return shifts;

    return shifts.filter(shift => {
      
      const shiftCreatedDate = dayjs(shift.createdAt);
      const weekStart = currentWeek[0];
      const weekEnd = currentWeek[6];

          // Departman filtresi kontrolü
    if (selectedDepartments?.length > 0) {
      const shiftDepartmentIds = shift.departments?.map((d: any) => d.id) || [];
      const hasMatchingDepartment = selectedDepartments.some(deptId => 
        shiftDepartmentIds.includes(deptId)
      );
      
      // Eğer vardiya hiç departmana sahip değilse, "Tüm Departmanlar" anlamında kabul et
      if (shiftDepartmentIds.length === 0) {
        // Tüm departmanları kabul et
      } else if (!hasMatchingDepartment) {
        return false;
      }
    }

    // Şube filtresi kontrolü
    if (selectedBranches?.length > 0) {
      const shiftBranchIds = shift.branches?.map((b: any) => b.id) || [];
      const hasMatchingBranch = selectedBranches.some(branchId => 
        shiftBranchIds.includes(branchId)
      );
      
      // Eğer vardiya hiç şubeye sahip değilse, "Tüm Şubeler" anlamında kabul et
      if (shiftBranchIds.length === 0) {
        // Tüm şubeleri kabul et
        } else if (!hasMatchingBranch) {
          return false;
        }
      }

      if (shift.validFrom && shift.validTo) {
        const validFrom = dayjs(shift.validFrom);
        const validTo = dayjs(shift.validTo);
        
        const isCurrentWeekInValidRange = (
          (weekStart.isBefore(validTo) && weekEnd.isAfter(validFrom)) ||
          weekStart.isSame(validFrom, 'day') || 
          weekStart.isSame(validTo, 'day') || 
          weekEnd.isSame(validFrom, 'day') || 
          weekEnd.isSame(validTo, 'day')
        );
        
        if (!isCurrentWeekInValidRange) {
          return false; 
        }
      }

      if (shift.status === 'active') return true;

      // Filtrelenmiş atamalarla kontrol et
      const hasAssignmentsInWeek = filteredWeeklyAssignments.some(user =>
        user.shifts.some(userShift => {
          if (userShift.shiftId !== shift.id) return false;
  
          const shiftStartDate = dayjs(userShift.startDate);
          const shiftEndDate = dayjs(userShift.endDate);
  
          if (shift.status === 'archived') {
            const archiveDate = dayjs(shift.updatedAt);
            if (shiftStartDate.isAfter(archiveDate)) {
              return false;
            }
          }
  
          return (
            (shiftStartDate.isSame(weekStart, 'day') || shiftStartDate.isAfter(weekStart)) &&
            (shiftEndDate.isSame(weekEnd, 'day') || shiftEndDate.isBefore(weekEnd))
          );
        })
      );
      return hasAssignmentsInWeek;
    });
  }, [shifts, filteredWeeklyAssignments, currentWeek,selectedDepartments,selectedBranches]);

  return (
    <>
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">{t("shifts.title")}</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">{t("shifts.breadcrumb.personnel")}</li>
                  <li className="breadcrumb-item active">{t("shifts.breadcrumb.shiftManagement")}</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="d-flex flex-column align-items-stretch w-100" style={{ gap: '12px' }}>
                <div className="d-flex flex-column flex-sm-row align-items-stretch" style={{ gap: '10px' }}>
                  <Button 
                    variant="info" 
                    className="d-flex align-items-center justify-content-center w-100"
                    onClick={openBulkAssignModal}
                    style={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="ti ti-calendar-plus me-2"></i>
                    <span>Toplu Atama</span>
                  </Button>
                  <Button 
                    variant="success" 
                    className="d-flex align-items-center justify-content-center w-100"
                    onClick={() => openShiftTypeModal()}
                    style={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="fa fa-cog me-2"></i>
                    <span>{t("shifts.shiftTypes")}</span>
                  </Button>
                </div>
                <div className="d-flex align-items-center w-100">
                  <DatePicker 
                    picker="week" 
                    locale={pickerLocale}
                    value={selectedDate}
                    onChange={(date) => date && setSelectedDate(date)}
                    format="YYYY - [Hafta] w"
                    allowClear={false}
                    className="flex-grow-1"
                    style={{
                      width: '100%',
                      marginBottom: '0px',
                    }}
                  />
                  <i className="ti ti-calendar ms-2 fs-3"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="card border-0 mb-4">
            <div className="card-body d-flex align-items-center justify-content-between flex-wrap pb-1">
              <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-filter fs-16 text-muted"></i>
                  <span className="text-muted fs-13 fw-medium">Filtreler:</span>
                </div>
                
                <div className="filter-group d-flex align-items-center gap-2">
                  <Select
                    mode="multiple"
                    placeholder="Departman Seç"
                    value={selectedDepartments}
                    onChange={handleDepartmentFilter}
                    style={{ minWidth: 180 }}
                    allowClear
                    maxTagCount={1}
                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} daha`}
                    size="middle"
                    tagRender={(props) => {
                      return (
                        <Tag color="#7338A0" closable onClose={props.onClose} style={{textAlign: 'center', justifyContent: 'center',alignItems: 'center',display: 'flex',flexWrap: 'nowrap'}}>
                          {props.label}
                        </Tag>
                      );
                    }}
                  >
                    {departments?.map((dept) => (
                      <Select.Option key={dept.id} value={dept.id}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{dept.name}</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="filter-group d-flex align-items-center gap-2">
                  <Select
                    mode="multiple"
                    placeholder="Şube Seç"
                    value={selectedBranches}
                    onChange={handleBranchFilter}
                    style={{ minWidth: 160}}
                    allowClear
                    maxTagCount={1}
                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} daha`}
                    size="middle"
                    tagRender={(props) => {
                      return (
                        <Tag color="#7338A0" closable onClose={props.onClose} style={{textAlign: 'center', justifyContent: 'center',alignItems: 'center',display: 'flex',flexWrap: 'nowrap'}}>
                          {props.label}
                        </Tag>
                      );
                    }}
                  >
                    {shops?.map((shop: any) => (
                      <Select.Option key={shop.id} value={shop.id}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{shop.name}</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {(selectedDepartments?.length > 0 || selectedBranches?.length > 0) && (
                  <button
                    onClick={clearFilters}
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    title="Filtreleri Temizle"
                  >
                    <i className="ti ti-x fs-12"></i>
                    Temizle
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* İstatistik Kartları */}
          <Row className="mb-4">
            <Col md={3}>
              <StatisticCard
                title={t("shifts.shiftType")}
                value={shifts.filter((shift: any) => shift.status==='active').length}
                icon={<i className="ti ti-alarm fs-3"></i>}
                iconBg="#4A6CF7"
              />
            </Col>
            <Col md={3}>
              <StatisticCard
                title={t("shifts.totalAssignments")}
                value={filteredWeeklyAssignments?.length}
                subtitle={`${t("shifts.weeklyShiftDistribution")}: ${currentWeek?.length > 0 ? filteredWeeklyAssignments?.length : 0}`}
                icon={<i className="ti ti-calendar-event fs-3"></i>}
                iconBg="#6AD2Ff"
              />
            </Col>
            <Col md={3}>
              <StatisticCard
                title={t("shifts.averageShiftsPerPerson")}
                value={(filteredWeeklyAssignments.length / (filteredUsers?.length || 1)).toFixed(1)}
                subtitle={t("shifts.averageShiftsPerPerson")}
                icon={<i className="ti ti-chart-bar fs-3"></i>}
                iconBg="#F7C32E"
              />
            </Col>
            <Col md={3}>
              <StatisticCard
                title={t("shifts.busiestDay")}
                value={currentWeek?.length > 0 ? (
                  [...currentWeek].map((day: any) => ({
                    day,
                    count: filteredWeeklyAssignments.reduce((total, user) => 
                      total + user.shifts.filter((shift: any) => 
                        dayjs(shift.startDate).format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
                      ).length
                    , 0)
                  })).sort((a, b) => b.count - a.count)[0]?.day.format('dddd') || "-"
                ) : "-"}
                icon={<i className="ti ti-stack-2 fs-3"></i>}
                iconBg="#4CAF50"
              />
            </Col>
          </Row>

          {/* Haftalık Takvim */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <h4 className="mb-0">{t("shifts.weeklyShiftCalendar")}</h4>
                <span className="ms-2 text-muted">
                  {currentWeek?.length > 0 ? (
                    `${currentWeek[0].locale(i18n.language).format('DD MMMM')} - ${currentWeek[6].locale(i18n.language).format('DD MMMM YYYY')}`
                  ) : (
                    t("shifts.loading.weeklyShiftDistribution")
                  )}
                </span>
              </div>
              <div className="d-flex">
              <Button 
                  variant="outline-info" 
                  className="me-2 d-flex align-items-center" 
                  onClick={captureScreenshot}
                >
                  <CameraOutlined className="me-1" /> PNG
                </Button>
                <Button 
                  variant="outline-danger" 
                  className="d-flex align-items-center me-2" 
                  onClick={exportToPdf}
                >
                  <FilePdfOutlined className="me-1" /> PDF
                </Button>
                <Button variant="light" className="me-2" onClick={goToPreviousWeek}>
                  <ArrowLeftOutlined />
                </Button>
                <Button variant="light" className="me-2" onClick={goToNextWeek}>
                  <ArrowRightOutlined />
                </Button>
                
              </div>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t("shifts.loading.weeklyShiftDistribution")}</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive" ref={calendarRef}>
                  <table className="table table-bordered shift-table">
                    <thead>
                      <tr>
                        <th style={{ width: '200px', backgroundColor: '#f8f9fa' }}>{t("shifts.shifts")}</th>
                        {currentWeek.map((day, index) => (
                          <th 
                            key={index} 
                            className="text-center"
                            style={{
                              backgroundColor: day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') 
                                ? '#e6f7ff' 
                                : day.day() === 0 || day.day() === 6 ? '#f9f9f9' : '#fff'
                            }}
                          >
                            <div className="mb-1 fw-bold">{day.format('dddd')}</div>
                            <div className="small text-muted">{day.format('DD MMMM')}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredShifts().map((shift: ShiftInfo) => (
                        <tr key={shift.id}>
                          <td 
                            className="align-middle fw-bold" 
                            style={{ 
                              backgroundColor: `${shiftColors[shift.id.toString()] || shiftColors.default}15`,
                              borderLeft: `4px solid ${shiftColors[shift.id.toString()] || shiftColors.default}`
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div 
                                className="rounded-circle me-2" 
                                style={{ 
                                  width: '12px', 
                                  height: '12px', 
                                  backgroundColor: shiftColors[shift.id.toString()] || shiftColors.default 
                                }}
                              ></div>
                              <div>
                                <div>{shift.name}</div>
                                <small className="text-muted">{shift.startTime} - {shift.endTime}</small>
                              </div>
                            </div>
                          </td>
                          
                          {/* Haftanın her günü için hücre oluştur */}
                          {currentWeek.map((day, dayIndex) => {
                            // Bu gün ve vardiya için atanan personeli bul
                            const dayStr = day.format('YYYY-MM-DD');
                            const cellAssignments = getFilteredDayAssignments(day).filter(a => a.shiftId === shift.id);
                            const isArchived = shift.status === 'archived';
                            
                            return (
                              <td 
                                key={dayIndex} 
                                className={`position-relative shift-cell ${isArchived ? 'archived-shift' : ''}`}
                                style={{
                                  backgroundColor: cellAssignments?.length > 0 
                                    ? `${shiftColors[shift.id.toString()] || shiftColors.default}10` 
                                    : 'transparent',
                                  cursor: isArchived ? 'not-allowed' : 'pointer',
                                  minHeight: '100px',
                                  opacity: isArchived ? 0.6 : 1
                                }}
                                onClick={() => {
                                  if (isArchived) {
                                    message.warning(t("shifts.error.archivedShift"));
                                    return;
                                  }
                                  setSelectedDay(day);
                                  setIsModalVisible(true);
                                  form.setFieldsValue({
                                    date: day,
                                    shiftId: shift.id
                                  });
                                  // Seçilen güne ait vardiyaları getir
                                  const assignmentsForDay = getFilteredDayAssignments(day);
                                  setDailyAssignments(assignmentsForDay.filter(a => a.shiftId === shift.id));
                                }}
                              >
                                {cellAssignments?.length > 0 ? (
                                  <div className="p-2">
                                    {cellAssignments?.slice(0, 3)?.map((assignment: any, idx: any) => (
                                      assignment?.userName && (
                                        <div 
                                          key={idx} 
                                          className="mb-1 p-1 rounded d-flex align-items-center"
                                          style={{ 
                                            backgroundColor: `${shiftColors[shift.id.toString()] || shiftColors.default}15`,
                                            border: `1px solid ${shiftColors[shift.id.toString()] || shiftColors.default}30`,
                                          }}
                                        >
                                          <div className="avatar-xs me-2">
                                            <div 
                                              className="rounded-circle d-flex align-items-center justify-content-center"
                                              style={{ 
                                                width: '24px', 
                                                height: '24px', 
                                                backgroundColor: shiftColors[shift.id.toString()] || shiftColors.default,
                                                color: 'white',
                                                fontSize: '10px',
                                                fontWeight: 'bold'
                                              }}
                                            >
                                              {assignment.userName.charAt(0)}
                                            </div>
                                          </div>
                                          <div className="text-truncate small" style={{ maxWidth: '120px' }}>
                                            {assignment.userName}
                                          </div>
                                        </div>
                                      )
                                    ))}
                                    
                                    {cellAssignments?.length > 3 && (
                                      <div className="text-center mt-1">
                                        <Badge 
                                          color={shiftColors[shift.id.toString()] || shiftColors.default} 
                                          text={`+ ${cellAssignments?.length - 3} ${t("shifts.table.more")}`} 
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 p-3 text-muted">
                                    <i className="ti ti-plus me-1"></i> {t("shifts.table.addShift")}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
          {/* Grafikler */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="card-title">{t("shifts.shiftDistribution")}</h5>
                  <div style={{ height: "250px" }}>
                    {shifts?.length > 0 ? (
                      <Pie 
                        data={filteredPieChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <p className="text-muted">{t("shifts.noShiftTypes")}</p>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="card-title">{t("shifts.weeklyShiftDistribution")}</h5>
                  <div style={{ height: "250px" }}>
                    {currentWeek?.length > 0 ? (
                      <Bar 
                        data={filteredBarChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <p className="text-muted">{t("shifts.loading.weeklyShiftDistribution")}</p>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
         

          {/* Haftalık Takvim */}
        </div>
      </div>

      {/* Vardiya Atama Modalı */}
      <Modal
        title={selectedDay ? `${selectedDay.format('DD MMMM YYYY - dddd')} Vardiya Ataması` : "Vardiya Ataması"}
        open={isModalVisible}
        onCancel={handleCancel}
        confirmLoading={isLoading}
        okText={t("shifts.modal.assignShift")}
        cancelText={t("shifts.modal.cancel")}
        onOk={handleAssignShift}
        width={1000}
      >
        <div className="mb-4">
          <h5>{t("shifts.modal.existingShifts")}</h5>
          <Table 
            dataSource={dailyAssignments}
            columns={shiftColumns}
            Selection={false}
          />
        </div>

        <h5>{t("shifts.modal.newShiftAssignment")}</h5>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ date: selectedDay }}
        >
          <Form.Item
            name="employeeId"
            label={t("shifts.modal.employee")}
            rules={[{ required: true, message: t("shifts.error.employeeRequired"),type: 'array',min: 1 }]}
          >
            <Select 
              placeholder={t("shifts.modal.employeePlaceholder")}
              loading={usersLoading}
              showSearch
              optionFilterProp="children"
              mode="multiple"
              maxTagCount={2}
              tagRender={(props) => {
                return (
                  <Tag color="#7338A0" closable onClose={props.onClose}>
                    {props.label}
                  </Tag>
                );
              }}
              maxTagPlaceholder={(omittedValues: any) => `+${omittedValues?.length} daha`}
            >
              {filteredUsers && filteredUsers.filter((user: User) => user.status === 'active' &&!user.isFixedShift).map((user: User) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} - {user.department?.map(d => d.name).join(', ')} - {user.branch?.map(b => b.name).join(', ')}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="shiftId"
            label={t("shifts.modal.shiftType")}
            rules={[{ required: true, message: t("shifts.error.shiftTypeRequired") }]}
          >
            <Select placeholder={t("shifts.modal.shiftTypePlaceholder")}>
              {getFilteredShifts().filter(shift => shift.status === 'active').map(shift => (
                <Select.Option key={shift.id} value={shift.id}>
                  <Badge 
                    color={shiftColors[shift?.id?.toString()] || shiftColors.default} 
                    text={`${shift.name} (${shift.startTime} - ${shift.endTime})`} 
                  />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            name="date" 
            label={t("shifts.modal.date")}
            rules={[{ required: true, message: t("shifts.error.dateRequired") }]}
          >
            <DatePicker 
              className="w-100" 
              locale={pickerLocale}
              format="DD.MM.YYYY"
              value={selectedDay || undefined}
            />
          </Form.Item>

          {/* <Row>
            <Col span={24}>
              <Form.Item
                name="timeRange"
                label={t("shifts.modal.timeRange")}
                rules={[{ required: false, message: t("shifts.error.timeRangeRequired") }]}
              >
                <AntTimePicker.RangePicker 
                  className="w-100"
                  format="HH:mm"
                  locale={pickerLocale}
                  placeholder={[t("shifts.modal.startTime"), t("shifts.modal.endTime")]}
                  onChange={(times) => {
                    if (times && times[0] && times[1]) {
                      form.setFieldsValue({
                        startTime: times[0],
                        endTime: times[1]
                      });
                    }
                  }}
                />
              </Form.Item>
              
              <Form.Item name="startTime" hidden={true} noStyle>
                <Input />
              </Form.Item>
              <Form.Item name="endTime" hidden={true} noStyle>
                <Input />
              </Form.Item>
            </Col>
          </Row> */}
        </Form>
      </Modal>

      {/* Vardiya Tipi Ekleme/Düzenleme Modalı */}
      <Modal
        title={editingShift ? t("shifts.modal.editShiftType") : t("shifts.modal.addShiftType")}
        open={isShiftTypeModalVisible}
        onCancel={() => setIsShiftTypeModalVisible(false)}
        confirmLoading={isLoading}
        okText={editingShift ? t("shifts.modal.update") : t("shifts.modal.add")}
        cancelText={t("shifts.modal.cancel")}
        onOk={handleShiftTypeSubmit}
        width={800}
      >
        <Form
          form={shiftTypeForm}
          layout="vertical"
          initialValues={{ 
            flexTime: 0,
            departments: ['all'],
            branches: ['all'],
            status: 'active'
          }}
        >
          <Form.Item
            name="name"
            label={t("shifts.modal.shiftName")}
            rules={[{ required: true, message: t("shifts.error.shiftNameRequired") }]}
          >
            <Input placeholder={t("shifts.modal.shiftNamePlaceholder")} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label={t("shifts.modal.startTime")}
                rules={[{ required: true, message: t("shifts.error.startTimeRequired") }]}
              >
                <AntTimePicker 
                  className="w-100"
                  format="HH:mm"
                  locale={pickerLocale}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label={t("shifts.modal.endTime")}
                rules={[{ required: true, message: t("shifts.error.endTimeRequired") }]}
              >
                <AntTimePicker 
                  className="w-100"
                  format="HH:mm"
                  locale={pickerLocale}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">Departmanlar</label>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted fs-13">Tümü</span>
                  <Switch
                    checked={isAllDepartments}
                    onChange={(checked) => {
                      setIsAllDepartments(checked);
                      if (checked) {
                        shiftTypeForm.setFieldValue('departments', []);
                      }
                    }}
                    size="small"
                  />
                </div>
              </div>
              <Form.Item
                name="departments"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!isAllDepartments && (!value || value.length === 0)) {
                        return Promise.reject(new Error('En az bir departman seçmelisiniz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder={isAllDepartments ? "Tüm departmanlar seçili" : "Departman seçiniz"}
                  allowClear
                  maxTagCount={2}
                  maxTagPlaceholder={(omittedValues: any) => `+${omittedValues?.length} daha`}
                  disabled={isAllDepartments}
                  tagRender={(props) => {
                    return (
                      <Tag color="#7338A0" closable onClose={props.onClose} style={{textAlign: 'center', justifyContent: 'center',alignItems: 'center',display: 'flex',flexWrap: 'nowrap'}}>
                        {props.label}
                      </Tag>
                    );
                  }}
                >
                  {departments?.map((dept) => (
                    <Select.Option key={dept.id} value={dept.id}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span>{dept.name}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">Şubeler</label>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted fs-13">Tümü</span>
                  <Switch
                    checked={isAllBranches}
                    onChange={(checked) => {
                      setIsAllBranches(checked);
                      if (checked) {
                        shiftTypeForm.setFieldValue('branches', []);
                      }
                    }}
                    size="small"
                  />
                </div>
              </div>
              <Form.Item
                name="branches"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!isAllBranches && (!value || value.length === 0)) {
                        return Promise.reject(new Error('En az bir şube seçmelisiniz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder={isAllBranches ? "Tüm şubeler seçili" : "Şube seçiniz"}
                  allowClear
                  maxTagCount={2}
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} daha`}
                  disabled={isAllBranches}
                  tagRender={(props) => {
                    return (
                      <Tag color="#7338A0" closable onClose={props.onClose} style={{textAlign: 'center', justifyContent: 'center',alignItems: 'center',display: 'flex',flexWrap: 'nowrap'}}>
                        {props.label}
                      </Tag>
                    );
                  }}
                >
                  {shops?.map((shop: any) => (
                    <Select.Option key={shop.id} value={shop.id}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span>{shop.name}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="validFrom"
                label={t("shifts.modal.validFrom")}
                rules={[{ required: true, message: t("shifts.error.validFromRequired") }]}
              >
                <DatePicker locale={pickerLocale} className="w-100" format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}> 
              <Form.Item
                name="validTo"
                label={t("shifts.modal.validTo")}
              >
                <DatePicker locale={pickerLocale} className="w-100" format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="status"
            label="Durum"
          >
            <Select placeholder="Durum seçiniz">
              <Select.Option value="active">
                <Badge status="success" text="Aktif" />
              </Select.Option>
              <Select.Option value="archived">
                <Badge status="default" text="Arşivlenmiş" />
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>

        {shifts?.length > 0 && (
          <div className="mt-4">
            <h5>{t("shifts.modal.existingShiftTypes")}</h5>
            <div style={{ maxHeight: '300px', overflow: 'auto' }}>
              <Table 
                dataSource={shifts}
                columns={shiftTypeColumns}
                Selection={false}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Toplu Vardiya Atama Modalı */}
      <Modal
        title="Toplu Vardiya Atama"
        open={isBulkAssignModalVisible}
        onCancel={() => setIsBulkAssignModalVisible(false)}
        confirmLoading={isLoading}
        okText="Toplu Atama Yap"
        cancelText="İptal"
        onOk={handleBulkAssignShift}
        width={600}
      >
        <Form
          form={bulkAssignForm}
          layout="vertical"
        >
          <Form.Item
            name="employeeIds"
            label="Çalışanlar"
            rules={[{ required: true, message: t("shifts.error.employeeRequired"), type: 'array', min: 1 }]}
          >
            <Select
              mode="multiple"
              placeholder="Çalışanları seçiniz"
              loading={usersLoading}
              showSearch
              optionFilterProp="children"
              maxTagCount={2}
              tagRender={(props) => {
                return (
                  <Tag color="#7338A0" closable onClose={props.onClose}>
                    {props.label}
                  </Tag>
                );
              }}
              maxTagPlaceholder={(omittedValues: any) => `+${omittedValues?.length} daha`}
            >
              {filteredUsers && filteredUsers.filter((user: User) => user.status === 'active' &&!user.isFixedShift).map((user: User) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} - {user.department?.map(d => d.name).join(', ')} - {user.branch?.map(b => b.name).join(', ')}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="shiftId"
            label="Vardiya Tipi"
            rules={[{ required: true, message: t("shifts.error.shiftTypeRequired") }]}
          >
            <Select placeholder="Vardiya tipi seçiniz">
              {getFilteredShifts().filter(shift => shift.status === 'active').map(shift => (
                <Select.Option key={shift.id} value={shift.id}>
                  <Badge 
                    color={shiftColors[shift?.id?.toString()] || shiftColors.default} 
                    text={`${shift.name} (${shift.startTime} - ${shift.endTime})`} 
                  />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Tarih Aralığı"
            rules={[{ required: true, message: "Lütfen tarih aralığı seçiniz" }]}
          >
            <DatePicker.RangePicker 
              className="w-100"
              locale={pickerLocale}
              format="DD.MM.YYYY"
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
            />
          </Form.Item>

          <Form.Item
            name="selectedDays"
            label="Günler"
            rules={[{ required: true, message: "Lütfen en az bir gün seçiniz" }]}
          >
            <Select
              mode="multiple"
              placeholder="Hangi günlerde atama yapılacağını seçiniz"
              style={{ width: '100%' }}
              onChange={(value) => setSelectedDays(value)}
              tagRender={(props) => {
                const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
                const dayName = dayNames[parseInt(props.value as string)];
                return (
                  <Tag color="#7338A0" closable onClose={props.onClose}>
                    {dayName}
                  </Tag>
                );
              }}
            >
              {[
                { name: 'Pazartesi', value: '1' },
                { name: 'Salı', value: '2' },
                { name: 'Çarşamba', value: '3' },
                { name: 'Perşembe', value: '4' },
                { name: 'Cuma', value: '5' },
                { name: 'Cumartesi', value: '6' },
                { name: 'Pazar', value: '0' }
              ].map((day) => (
                <Select.Option key={day.value} value={day.value}>
                  {day.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ShiftPage;
